import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "glass-navbar" : "bg-transparent"
      }`}
    >
      <div className="tonal-shift">
        <div className="flex justify-between items-center max-w-7xl mx-auto px-6 lg:px-8 h-20">
          {/* Logo */}
          <Link
            to="/"
            className="text-xl font-bold tracking-headline text-[var(--primary)] font-headline uppercase hover:brightness-110 transition-all"
          >
            Kravion
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/services"
              className={`font-headline tracking-tight text-sm uppercase transition-all duration-300 hover:brightness-125 ${
                isActive("/services")
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)] pb-1"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Services
            </Link>
            <Link
              to="/work"
              className={`font-headline tracking-tight text-sm uppercase transition-all duration-300 hover:brightness-125 ${
                isActive("/work") || location.pathname.startsWith("/work/")
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)] pb-1"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Work
            </Link>
            <Link
              to="/pricing"
              className={`font-headline tracking-tight text-sm uppercase transition-all duration-300 hover:brightness-125 ${
                isActive("/pricing")
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)] pb-1"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className={`font-headline tracking-tight text-sm uppercase transition-all duration-300 hover:brightness-125 ${
                isActive("/blog") || location.pathname.startsWith("/blog/")
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)] pb-1"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Blog
            </Link>
            <Link
              to="/live-projects"
              className={`font-headline tracking-tight text-sm uppercase transition-all duration-300 hover:brightness-125 ${
                isActive("/live-projects")
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)] pb-1"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Live Projects
            </Link>
            <Link
              to="/contact"
              className={`font-headline tracking-tight text-sm uppercase transition-all duration-300 hover:brightness-125 ${
                isActive("/contact")
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)] pb-1"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Contact
            </Link>
          </div>

          {/* CTA + Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              className="hidden sm:block magnetic bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-6 py-2 font-headline font-bold text-sm uppercase tracking-code transition-all duration-200 ease-in-out hover:brightness-110 active:scale-95 rounded-none"
            >
              Start a Project
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[var(--surface-container)] border-t border-[var(--outline-variant)]/20">
            <div className="px-6 py-6 space-y-4">
              <Link
                to="/services"
                className="block w-full text-left font-headline text-sm uppercase text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors py-2"
              >
                Services
              </Link>
              <Link
                to="/work"
                className="block w-full text-left font-headline text-sm uppercase text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors py-2"
              >
                Work
              </Link>
              <Link
                to="/pricing"
                className="block w-full text-left font-headline text-sm uppercase text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors py-2"
              >
                Pricing
              </Link>
              <Link
                to="/blog"
                className="block w-full text-left font-headline text-sm uppercase text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors py-2"
              >
                Blog
              </Link>
              <Link
                to="/live-projects"
                className="block w-full text-left font-headline text-sm uppercase text-[var(--on-surface)] hover:text-[var(--primary)] transition-colors py-2"
              >
                Live Projects
              </Link>
              <Link
                to="/contact"
                className="block w-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-6 py-3 font-headline font-bold text-sm uppercase tracking-code transition-all duration-200 ease-in-out hover:brightness-110 active:scale-95 rounded-none mt-4"
              >
                Start a Project
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
